import React, { useState, useEffect } from 'react';
import MainLayout from '../components/MainLayout';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ShareIcon from '@mui/icons-material/Share';
import CloseIcon from '@mui/icons-material/Close';
import PersonIcon from '@mui/icons-material/Person';
import VideoIcon from '@mui/icons-material/VideoLibrary';
import {
  Paper,
  Typography,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Card,
  CardContent,
  CircularProgress,
  Chip,
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Divider,
  TablePagination,
  TextField,
} from '@mui/material';
import CircleIcon from '@mui/icons-material/Circle';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';

import { reportsAPI, linksAPI } from '../services/api';
import io from 'socket.io-client';

const ReportsPage = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({
    summary: {
      totalPeople: 0,
      totalVideos: 0,
      totalViews: 0,
      sharedLinks: 0,
    },
    sharingStatus: [],
    viewsByVideo: [],
  });
  const [recentLinks, setRecentLinks] = useState<any[]>([]);

  // Detay Modalı State
  const [selectedLink, setSelectedLink] = useState<any>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [linkDetails, setLinkDetails] = useState<any>(null);
  const [detailsLoading, setDetailsLoading] = useState(false);

  // Canlı İzleyici State
  const [activeViewers, setActiveViewers] = useState<any[]>([]);
  const prevViewerCountRef = React.useRef(0);
  const audioContextRef = React.useRef<AudioContext | null>(null);
  const audioUnlockedRef = React.useRef(false);

  // Paylaşım Raporu - Sıralama, Filtreleme ve Sayfalama
  const [sortField, setSortField] = useState<string>('person_name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [filterPerson, setFilterPerson] = useState('');
  const [filterVideo, setFilterVideo] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // AudioContext'i kullanıcı etkileşiminden sonra oluştur (browser policy)
  React.useEffect(() => {
    const unlockAudio = () => {
      if (!audioUnlockedRef.current) {
        audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();
        audioUnlockedRef.current = true;
        console.log('🔊 Ses sistemi etkinleştirildi');
      }
      if (audioContextRef.current?.state === 'suspended') {
        audioContextRef.current.resume();
      }
    };
    document.addEventListener('click', unlockAudio);
    document.addEventListener('keydown', unlockAudio);
    return () => {
      document.removeEventListener('click', unlockAudio);
      document.removeEventListener('keydown', unlockAudio);
    };
  }, []);

  // Yeni izleyici için ses bildirimi
  const playNotificationSound = async () => {
    try {
      const audioContext = audioContextRef.current;
      if (!audioContext) return;

      // AudioContext suspended ise resume et
      if (audioContext.state === 'suspended') {
        await audioContext.resume();
      }

      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);

      oscillator.frequency.value = 880; // Daha yüksek frekans (A5 notası)
      oscillator.type = 'sine';
      gainNode.gain.value = 0.5; // Daha yüksek ses

      oscillator.start();
      // Kısa bip
      setTimeout(() => {
        oscillator.stop();
      }, 200);

      console.log('🔔 Bildirim sesi çalındı!');
    } catch (e) {
      console.log('Ses çalınamadı:', e);
    }
  };

  useEffect(() => {
    // Socket.io Bağlantısı - polling ve websocket destekle
    const socket = io('https://video.banasesver.com', {
      transports: ['polling', 'websocket'], // Önce polling, sonra websocket upgrade
      reconnectionAttempts: 5,
      timeout: 20000
    });

    socket.on('connect', () => {
      console.log('Socket bağlantısı kuruldu:', socket.id);
    });

    socket.on('connect_error', (err: Error) => {
      console.error('Socket bağlantı hatası:', err.message);
    });

    socket.on('active_viewers_update', (viewers: any[]) => {
      console.log('Canlı izleyici güncellemesi:', viewers);

      // Yeni izleyici geldi mi kontrol et
      if (viewers.length > prevViewerCountRef.current) {
        playNotificationSound();
        // Görsel bildirim olarak da title'ı değiştir
        document.title = `🔔 Yeni İzleyici! - BSV Video`;
        setTimeout(() => {
          document.title = 'Raporlar - BSV Video';
        }, 3000);
      }
      prevViewerCountRef.current = viewers.length;

      setActiveViewers(viewers);
    });

    return () => {
      socket.disconnect();
    };
  }, []);

  // Log Gruplama Fonksiyonu
  const groupLogsBySession = (logs: any[]) => {
    if (!logs || logs.length === 0) return [];

    const sortedLogs = [...logs].sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
    const sessions: any[] = [];
    let currentSession: any[] = [];

    sortedLogs.forEach((log, index) => {
      // Eğer log bir 'open' türündeyse veya önceki log ile arasında çok zaman farkı varsa yeni oturum kabul et
      // Ancak buradaki basit mantık: 'open' olayları oturum başlatıcısıdır.
      // Ya da tersten gidiyoruz (yeni -> eski): 'open' gördüğümüzde oturumu kapatıp listeye ekleyebiliriz.

      currentSession.push(log);

      // Eğer bu 'open' ise veya listenin son elemanıysa bu oturumu bitir
      if (log.event_type === 'open' || index === sortedLogs.length - 1) {
        sessions.push(currentSession);
        currentSession = [];
      }
    });

    return sessions;
  };

  const handleOpenDetails = async (link: any) => {
    setSelectedLink(link);
    setDetailsOpen(true);
    setDetailsLoading(true);
    try {
      const response = await linksAPI.getStats(link.id);
      setLinkDetails(response.data);
    } catch (error) {
      console.error('Link detayları alınamadı:', error);
    } finally {
      setDetailsLoading(false);
    }
  };

  const handleCloseDetails = () => {
    setDetailsOpen(false);
    setLinkDetails(null);
    setSelectedLink(null);
  };

  // İzlenme aralığı hesaplama fonksiyonu (oturum bazlı)
  const calculateWatchRanges = (link: any) => {
    // Eğer link detayları yoksa varsayılan değerler döndür
    if (!link.logs || link.logs.length === 0) {
      return {
        range_0_50: 0,
        range_51_75: 0,
        range_76_99: 0,
        range_100: 0
      };
    }

    const ranges = {
      range_0_50: 0,
      range_51_75: 0,
      range_76_99: 0,
      range_100: 0
    };

    // Logları zamana göre sırala (yeniden eskiye)
    const sortedLogs = [...link.logs].sort((a, b) =>
      new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    );

    // Oturumları grupla (open event'i oturum başlangıcıdır)
    const sessions: any[][] = [];
    let currentSession: any[] = [];

    sortedLogs.forEach((log, index) => {
      currentSession.push(log);

      // 'open' event'i veya son eleman ise oturumu kapat
      if (log.event_type === 'open' || index === sortedLogs.length - 1) {
        if (currentSession.length > 0) {
          sessions.push([...currentSession]);
        }
        currentSession = [];
      }
    });

    // Her oturum için en yüksek izlenme yüzdesini bul
    sessions.forEach(session => {
      let maxWatchTime = 0;
      let hasEnd = false;

      session.forEach(log => {
        if (log.event_type === 'end') {
          hasEnd = true;
          maxWatchTime = 100;
        } else if (log.event_type === 'progress' && log.watch_time) {
          const watchTime = parseInt(log.watch_time) || 0;
          if (watchTime > maxWatchTime) {
            maxWatchTime = watchTime;
          }
        }
      });

      // Oturumun en yüksek izlenme yüzdesine göre kategorize et
      if (hasEnd || maxWatchTime === 100) {
        ranges.range_100++;
      } else if (maxWatchTime >= 76 && maxWatchTime <= 99) {
        ranges.range_76_99++;
      } else if (maxWatchTime >= 51 && maxWatchTime <= 75) {
        ranges.range_51_75++;
      } else if (maxWatchTime >= 0 && maxWatchTime <= 50) {
        ranges.range_0_50++;
      }
    });

    return ranges;
  };

  // Sıralama fonksiyonu
  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Sayfalama fonksiyonları
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Filtrelenmiş ve sıralanmış linkler
  const getFilteredAndSortedLinks = () => {
    return recentLinks
      .filter(link => {
        const matchesPerson = !filterPerson ||
          link.person_name?.toLowerCase().includes(filterPerson.toLowerCase());
        const matchesVideo = !filterVideo ||
          link.video_title?.toLowerCase().includes(filterVideo.toLowerCase());
        return matchesPerson && matchesVideo;
      })
      .sort((a, b) => {
        let aValue: any;
        let bValue: any;

        // İzlenme aralığı sütunları için özel işlem
        if (sortField === 'range_0_50' || sortField === 'range_51_75' || sortField === 'range_76_99') {
          const aRanges = calculateWatchRanges(a);
          const bRanges = calculateWatchRanges(b);
          aValue = aRanges[sortField as keyof typeof aRanges];
          bValue = bRanges[sortField as keyof typeof bRanges];
        } else {
          aValue = a[sortField];
          bValue = b[sortField];
        }

        // String değerler için case-insensitive karşılaştırma
        if (typeof aValue === 'string') aValue = aValue.toLowerCase();
        if (typeof bValue === 'string') bValue = bValue.toLowerCase();

        if (aValue === bValue) return 0;
        if (aValue === undefined || aValue === null) return 1;
        if (bValue === undefined || bValue === null) return -1;

        const comparison = aValue > bValue ? 1 : -1;
        return sortDirection === 'asc' ? comparison : -comparison;
      });
  };

  const filteredLinks = getFilteredAndSortedLinks();
  const paginatedLinks = filteredLinks.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  useEffect(() => {
    const fetchReports = async () => {
      try {
        const [summaryRes, linksRes] = await Promise.all([
          reportsAPI.getSummary(),
          linksAPI.getAll()
        ]);

        setData(summaryRes.data);
        // Tüm linkleri al (sayfalama frontend'de yapılacak)
        setRecentLinks(linksRes.data);
        setLoading(false);
      } catch (error) {
        console.error('Raporlar alınırken hata:', error);
        setLoading(false);
      }
    };

    fetchReports();
  }, []);

  const getStatus = (devices: any, cities: any) => {
    const d = parseInt(devices) || 0;
    const c = parseInt(cities) || 0;

    if (d >= 3 || c >= 2) return 'paylaşılmış';
    if (d >= 2) return 'şüpheli';
    return 'normal';
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'normal':
        return 'success';
      case 'şüpheli':
        return 'warning';
      case 'paylaşılmış':
        return 'error';
      default:
        return 'default';
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="60vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <Typography variant="h4" component="h1" gutterBottom>
        Raporlar
      </Typography>
      <Typography variant="body1" color="text.secondary" paragraph>
        Video izlenme ve paylaşım istatistikleri.
      </Typography>

      <Box sx={{ mt: 2 }}>
        <Stack spacing={3}>
          {/* Canlı İzleyenler Kartı (EN ÜSTTE) */}
          <Box sx={{ width: '100%', mb: 2 }}>
            <Card sx={{ border: '1px solid #f0c14b' }}>
              <CardContent>
                <Box display="flex" alignItems="center" mb={2}>
                  <CircleIcon sx={{ color: '#f0c14b', fontSize: 16, mr: 1, animation: 'pulse 1.5s infinite' }} />
                  <Typography variant="h6" component="div">
                    Şu An İzleyenler ({activeViewers.length})
                  </Typography>
                </Box>
                {activeViewers.length > 0 ? (
                  <List sx={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: 2 }}>
                    {activeViewers.map((viewer, index) => (
                      <Paper key={index} variant="outlined" sx={{ p: 1, minWidth: 200, display: 'flex', alignItems: 'center' }}>
                        <ListItemAvatar>
                          <Avatar sx={{ bgcolor: 'secondary.main' }}>
                            <PersonIcon />
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={viewer.personName || 'Anonim'}
                          secondary={
                            <>
                              <Typography component="span" variant="body2" color="text.primary">
                                {viewer.videoSlug}
                              </Typography>
                              <br />
                              {viewer.city} • {viewer.device}
                            </>
                          }
                        />
                      </Paper>
                    ))}
                  </List>
                ) : (
                  <Typography color="text.secondary" variant="body2">
                    Şu an aktif izleyici yok.
                  </Typography>
                )}
                <style>
                  {`
                        @keyframes pulse {
                            0% { opacity: 1; }
                            50% { opacity: 0.4; }
                            100% { opacity: 1; }
                        }
                    `}
                </style>
              </CardContent>
            </Card>
          </Box>


          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={{ xs: 1, sm: 2, md: 3 }}
            useFlexGap
            flexWrap="wrap"
          >
            {/* Özet Kartları */}
            <Box sx={{ minWidth: { xs: '100%', sm: '48%', md: '23%' } }}>
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="center">
                    <PersonIcon color="primary" sx={{ fontSize: 40, mr: 2 }} />
                    <Box>
                      <Typography variant="h4" component="div">
                        {data.summary.totalPeople}
                      </Typography>
                      <Typography color="text.secondary">
                        Toplam Kişi
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Box>

            <Box sx={{ minWidth: { xs: '100%', sm: '48%', md: '23%' } }}>
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="center">
                    <VideoIcon color="primary" sx={{ fontSize: 40, mr: 2 }} />
                    <Box>
                      <Typography variant="h4" component="div">
                        {data.summary.totalVideos}
                      </Typography>
                      <Typography color="text.secondary">
                        Toplam Video
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Box>

            <Box sx={{ minWidth: { xs: '100%', sm: '48%', md: '23%' } }}>
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="center">
                    <VisibilityIcon color="primary" sx={{ fontSize: 40, mr: 2 }} />
                    <Box>
                      <Typography variant="h4" component="div">
                        {data.summary.totalViews}
                      </Typography>
                      <Typography color="text.secondary">
                        Toplam İzlenme
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Box>

            <Box sx={{ minWidth: { xs: '100%', sm: '48%', md: '23%' } }}>
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="center">
                    <ShareIcon color="warning" sx={{ fontSize: 40, mr: 2 }} />
                    <Box>
                      <Typography variant="h4" component="div">
                        {data.summary.sharedLinks}
                      </Typography>
                      <Typography color="text.secondary">
                        Şüpheli/Paylaşılmış Link
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Box>
          </Stack>
        </Stack>
      </Box>

      <Box sx={{ mt: 2 }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 2, md: 3 }}
        >
          {/* Paylaşım Durumu Grafiği */}
          <Box sx={{ width: { xs: '100%', md: '33.33%' } }}>
            <Paper sx={{ p: 3, height: '100%' }}>
              <Typography variant="h6" gutterBottom>
                Paylaşım Durumu
              </Typography>
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={data.sharingStatus}
                    cx="50%"
                    cy="50%"
                    innerRadius={40}
                    outerRadius={80}
                    fill="#8884d8"
                    paddingAngle={5}
                    dataKey="value"
                  >
                    {data.sharingStatus.map((entry: any, index: number) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
              <Box display="flex" justifyContent="space-around" mt={2}>
                {data.sharingStatus.map((entry: any) => (
                  <Box key={entry.name} display="flex" alignItems="center">
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        borderRadius: '50%',
                        bgcolor: entry.color,
                        mr: 1,
                      }}
                    />
                    <Typography variant="body2">{entry.name}: {entry.value}</Typography>
                  </Box>
                ))}
              </Box>
            </Paper>
          </Box>

          {/* Video İzlenme Grafiği */}
          <Box sx={{ width: { xs: '100%', md: '66.66%' } }}>
            <Paper sx={{ p: 3, height: '100%' }}>
              <Typography variant="h6" gutterBottom>
                Video İzlenme İstatistikleri
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart
                  data={data.viewsByVideo}
                  margin={{
                    top: 20,
                    right: 30,
                    left: 20,
                    bottom: 5,
                  }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="views" fill="#f0c14b" name="İzlenme" />
                </BarChart>
              </ResponsiveContainer>
            </Paper>
          </Box>
        </Stack>
      </Box>

      <Box sx={{ width: '100%' }}>
        <Paper sx={{ p: 3, mt: 3 }}>
          <Typography variant="h6" gutterBottom>
            Paylaşım Raporu
          </Typography>

          {/* Filtreleme Alanları */}
          <Box display="flex" gap={2} mb={3}>
            <TextField
              label="Kişi Ara"
              variant="outlined"
              size="small"
              value={filterPerson}
              onChange={(e) => setFilterPerson(e.target.value)}
              sx={{ minWidth: 200 }}
            />
            <TextField
              label="Video Ara"
              variant="outlined"
              size="small"
              value={filterVideo}
              onChange={(e) => setFilterVideo(e.target.value)}
              sx={{ minWidth: 200 }}
            />
            <Typography variant="body2" color="text.secondary" sx={{ alignSelf: 'center', ml: 'auto' }}>
              Toplam: {filteredLinks.length} kayıt
            </Typography>
          </Box>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell
                    style={{ cursor: 'pointer', fontWeight: 'bold' }}
                    onClick={() => handleSort('person_name')}
                  >
                    Kişi {sortField === 'person_name' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </TableCell>
                  <TableCell
                    style={{ cursor: 'pointer', fontWeight: 'bold' }}
                    onClick={() => handleSort('video_title')}
                  >
                    Video {sortField === 'video_title' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </TableCell>
                  <TableCell
                    style={{ cursor: 'pointer', fontWeight: 'bold' }}
                    onClick={() => handleSort('devices')}
                  >
                    Cihaz Sayısı {sortField === 'devices' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </TableCell>
                  <TableCell
                    style={{ cursor: 'pointer', fontWeight: 'bold' }}
                    onClick={() => handleSort('cities')}
                  >
                    Şehir Sayısı {sortField === 'cities' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </TableCell>
                  <TableCell
                    align="center"
                    style={{ cursor: 'pointer', fontWeight: 'bold' }}
                    onClick={() => handleSort('range_0_50')}
                  >
                    0-50% İzlenme {sortField === 'range_0_50' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </TableCell>
                  <TableCell
                    align="center"
                    style={{ cursor: 'pointer', fontWeight: 'bold' }}
                    onClick={() => handleSort('range_51_75')}
                  >
                    51-75% İzlenme {sortField === 'range_51_75' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </TableCell>
                  <TableCell
                    align="center"
                    style={{ cursor: 'pointer', fontWeight: 'bold' }}
                    onClick={() => handleSort('range_76_99')}
                  >
                    76-99% İzlenme {sortField === 'range_76_99' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </TableCell>
                  <TableCell
                    align="center"
                    style={{ cursor: 'pointer', fontWeight: 'bold' }}
                    onClick={() => handleSort('completed_views')}
                  >
                    %100 İzlenme {sortField === 'completed_views' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </TableCell>
                  <TableCell>Paylaşım Durumu</TableCell>
                  <TableCell align="right">İşlemler</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedLinks.map((link) => {
                  const status = getStatus(link.devices, link.cities);
                  const ranges = calculateWatchRanges(link);
                  return (
                    <TableRow key={link.id} hover>
                      <TableCell>{link.person_name}</TableCell>
                      <TableCell>{link.video_title}</TableCell>
                      <TableCell align="center">{link.devices || 0}</TableCell>
                      <TableCell align="center">{link.cities || 0}</TableCell>
                      <TableCell align="center">{ranges.range_0_50}</TableCell>
                      <TableCell align="center">{ranges.range_51_75}</TableCell>
                      <TableCell align="center">{ranges.range_76_99}</TableCell>
                      <TableCell align="center">{ranges.range_100}</TableCell>
                      <TableCell>
                        <Chip
                          label={status.charAt(0).toUpperCase() + status.slice(1)}
                          color={getStatusColor(status) as any}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => handleOpenDetails(link)}
                        >
                          Detaylar
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {paginatedLinks.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={10} align="center" sx={{ py: 3 }}>
                      <Typography color="text.secondary">
                        {filterPerson || filterVideo ? 'Filtreye uygun kayıt bulunamadı.' : 'Kayıt bulunamadı.'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Sayfalama */}
          <TablePagination
            component="div"
            count={filteredLinks.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[10, 25, 50, 100]}
            labelRowsPerPage="Sayfa başına satır:"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} / ${count}`}
          />
        </Paper>
      </Box>

      {/* Detay Modalı */}
      <Dialog
        open={detailsOpen}
        onClose={handleCloseDetails}
        maxWidth="md"
        fullWidth
        scroll="paper" // Modalın kendi içinde scroll olmasını sağlar
      >
        <DialogTitle display="flex" justifyContent="space-between" alignItems="center">
          {selectedLink?.person_name} - {selectedLink?.video_title}
          <IconButton onClick={handleCloseDetails}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ maxHeight: '70vh' }}>
          {detailsLoading ? (
            <Box display="flex" justifyContent="center" p={3}><CircularProgress /></Box>
          ) : linkDetails ? (
            <Stack spacing={3}>
              {/* Üst Bilgiler */}
              <Box display="flex" justifyContent="space-between" flexWrap="wrap" gap={2}>
                <Paper variant="outlined" sx={{ p: 2, flex: 1, minWidth: 150, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">Durum</Typography>
                  <Chip
                    label={linkDetails?.sharing_status?.toUpperCase()}
                    color={getStatusColor(linkDetails?.sharing_status) as any}
                    sx={{ mt: 1 }}
                  />
                </Paper>
                <Paper variant="outlined" sx={{ p: 2, flex: 1, minWidth: 150, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">İzlenme</Typography>
                  <Typography variant="h6">
                    {linkDetails.has_completed ? 'Tamamlandı' : linkDetails.has_started ? 'Başladı' : linkDetails.has_opened ? 'Açıldı' : 'İzlenmedi'}
                  </Typography>
                </Paper>
                <Paper variant="outlined" sx={{ p: 2, flex: 1, minWidth: 150, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">Şehir Sayısı</Typography>
                  <Typography variant="h6">{linkDetails.cities_used}</Typography>
                </Paper>
              </Box>

              {/* İstatistik Özetleri */}
              <Box display="flex" justifyContent="space-between" flexWrap="wrap" gap={2}>
                <Paper variant="outlined" sx={{ p: 2, flex: 1, minWidth: 100, textAlign: 'center', borderLeft: '4px solid #1976d2' }}>
                  <Typography variant="body2" color="text.secondary">Toplam Oturum</Typography>
                  <Typography variant="h6" color="primary.main">{groupLogsBySession(linkDetails.logs).length}</Typography>
                </Paper>
                <Paper variant="outlined" sx={{ p: 2, flex: 1, minWidth: 100, textAlign: 'center', borderLeft: '4px solid #ed6c02' }}>
                  <Typography variant="body2" color="text.secondary">Sayfa Açılışı</Typography>
                  <Typography variant="h6" sx={{ color: '#ed6c02' }}>{linkDetails.logs.filter((l: any) => l.event_type === 'open').length}</Typography>
                </Paper>
                <Paper variant="outlined" sx={{ p: 2, flex: 1, minWidth: 100, textAlign: 'center', borderLeft: '4px solid #2e7d32' }}>
                  <Typography variant="body2" color="text.secondary">Tam İzlenme (%100)</Typography>
                  <Typography variant="h6" color="success.main">
                    {linkDetails.logs.filter((l: any) => (l.event_type === 'end' || (l.event_type === 'progress' && l.watch_time === 100))).length}
                  </Typography>
                </Paper>
              </Box>

              {/* Log Listesi (Gruplanmış) */}
              <Typography variant="h6">İzleme Hareketleri</Typography>
              <Box>
                {groupLogsBySession(linkDetails.logs).map((session, sIndex) => {
                  const startDate = new Date(session[session.length - 1].created_at);
                  return (
                    <Card key={sIndex} variant="outlined" sx={{ mb: 2 }}>
                      <CardContent>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                          Oturum Başlangıcı: {startDate.toLocaleString('tr-TR')}
                        </Typography>
                        <TableContainer>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>Zaman</TableCell>
                                <TableCell>Olay</TableCell>
                                <TableCell>İlerleme</TableCell>
                                <TableCell>Cihaz</TableCell>
                                <TableCell>Şehir</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {session.map((log: any, lIndex: number) => (
                                <TableRow key={lIndex}>
                                  <TableCell>{new Date(log.created_at).toLocaleTimeString('tr-TR')}</TableCell>
                                  <TableCell>
                                    {log.event_type === 'open' && <Chip label="Açıldı" size="small" />}
                                    {log.event_type === 'play' && <Chip label="Başladı" color="primary" size="small" />}
                                    {log.event_type === 'progress' && <Chip label="İzleniyor" color="info" size="small" />}
                                    {log.event_type === 'end' && <Chip label="Bitti" color="success" size="small" />}
                                  </TableCell>
                                  <TableCell>{log.watch_time > 0 ? `%${log.watch_time}` : '-'}</TableCell>
                                  <TableCell>{log.browser} / {log.os}</TableCell>
                                  <TableCell>{log.city}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </CardContent>
                    </Card>
                  );
                })}
                {linkDetails.logs.length === 0 && (
                  <Typography align="center">Henüz hareket yok.</Typography>
                )}
              </Box>
            </Stack>
          ) : (
            <Typography>Veri bulunamadı.</Typography>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ReportsPage;